import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setStoredToken } from "@/lib/api";
import useAuthenticatedPhoto from "../lib/useAuthenticatedPhoto";
import { isCinematicPath } from "@/lib/cinematicRoutes";

const AuthCtx = createContext(null);

// Codespaces preview identity is deliberately frontend-only. It is enabled
// automatically on GitHub Codespaces forwarded hosts (or explicitly via
// REACT_APP_CODESPACE_PREVIEW=true) and never contacts the backend.
// This keeps visual review isolated from payroll, MongoDB, Google OAuth,
// and live credentials while preserving production authentication unchanged.
const CODESPACE_PREVIEW_USER = {
  user_id: "codespace-preview-damon",
  name: "Damon Mitchell",
  email: "damon.mitchell@codespace.preview",
  role: "boutique_manager",
  boutique_code: "PLT",
  boutique_name: "Plantation",
  team: "Team Green",
};

function detectCodespacePreview() {
  if (process.env.REACT_APP_CODESPACE_PREVIEW === "true") return true;
  if (typeof window === "undefined") return false;
  const host = window.location.hostname || "";
  return host.endsWith(".app.github.dev") || host.endsWith(".githubpreview.dev");
}

const isCodespacePreview = detectCodespacePreview();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(isCodespacePreview ? CODESPACE_PREVIEW_USER : null);
  const navigationPhoto = useAuthenticatedPhoto(user?.employee_profile?.picture ?? user?.picture);
  const [loading, setLoading] = useState(!isCodespacePreview);

  const refresh = useCallback(async () => {
    if (isCodespacePreview) {
      setUser(CODESPACE_PREVIEW_USER);
      setLoading(false);
      return;
    }
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isCodespacePreview) return;
    // If returning from OAuth callback, skip /me — AuthCallback handles session_id first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Cinematic routes (/present, /lab/*) run without auth by design.
    // Skip the /auth/me probe so the network tab stays quiet during
    // live keynotes and observation sessions.
    if (isCinematicPath(window.location.pathname)) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = async ({ afterSessionCleared } = {}) => {
    if (isCodespacePreview) {
      setUser(CODESPACE_PREVIEW_USER);
      return;
    }
    try { await api.post("/auth/logout"); } catch {}
    setStoredToken(null);
    setUser(null);
    // Session is already revoked and local identity cleared. Preserve only
    // the outgoing button artwork while the protected route unmounts.
    if (afterSessionCleared) {
      try { await afterSessionCleared(); } catch {}
    }
    window.location.href = "/login";
  };

  return (
    <AuthCtx.Provider value={{ user, loading, refresh, logout, navigationPhoto }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
